import { Injectable } from '@angular/core';
import { DialogService } from './dialog.service';
import { ToothDataNew } from '../models/order.models';

@Injectable()  // component-scoped — add to providers: [] in the component decorator
export class AbutmentService {

  abutmentTeeth: any[] = [];
  abutmentSelectedTeeth: number[] = [];
  teethWithCompleteData: number[] = [];
  submittedTeeth: number[] = [];

  constructor(private dialogService: DialogService) {}

  setAbutmentTeeth(toothData: ToothDataNew[]): void {
    this.abutmentTeeth = toothData?.filter((t: any) => t.type === 'Abutment') ?? [];
    this.abutmentSelectedTeeth = [];
    if (this.abutmentTeeth.length === 1) {
      this.abutmentSelectedTeeth.push(this.abutmentTeeth[0].teeth);
    }
  }

  setAbutmentSelectedTooth(tooth: number, toothData: ToothDataNew[]): void {
    const index = this.abutmentSelectedTeeth.indexOf(tooth);
    if (index > -1) {
      this.abutmentSelectedTeeth.splice(index, 1);
    } else {
      this.abutmentSelectedTeeth.push(tooth);
    }
    this.updateFilledTeethCount(toothData);
  }

  updateFilledTeethCount(toothData: ToothDataNew[]): void {
    this.teethWithCompleteData = this.abutmentSelectedTeeth.filter(toothNum => {
      const data = toothData.find(t => t.teeth === toothNum);
      return data &&
        data['abutmentStep1'] &&
        data['abutmentStep2'] &&
        data['abutmentStep3'] &&
        data['abutmentStep4'];
    });
  }

  areAllTeethSelected(): boolean {
    return this.abutmentTeeth.length > 0 &&
      this.abutmentSelectedTeeth.length === this.abutmentTeeth.length;
  }

  toggleSelectAll(checked: boolean): void {
    if (checked) {
      this.abutmentSelectedTeeth = this.abutmentTeeth.map(t => t.teeth);
    } else {
      this.abutmentSelectedTeeth = [];
    }
  }

  validateAbutmentSteps(toothData: ToothDataNew[], isAbutmentSelect: boolean): boolean {
    if (!this.abutmentTeeth || this.abutmentTeeth.length === 0) {
      this.dialogService.openMessageDialog('Warning!', 'No teeth available.').subscribe();
      return false;
    }

    const incompleteTeeth: number[] = [];
    this.abutmentTeeth.forEach(tooth => {
      const td = toothData?.find(t => t.teeth === tooth.teeth);
      const isComplete = td && td['abutmentStep1'] && td['abutmentStep2'] && td['abutmentStep3'] && td['abutmentStep4'];
      if (!isComplete) incompleteTeeth.push(tooth.teeth);
    });

    if (this.submittedTeeth.length !== this.abutmentTeeth.length && isAbutmentSelect) {
      this.dialogService.openMessageDialog('Warning!', 'Please fill all teeth details!').subscribe();
      return false;
    }

    if (incompleteTeeth.length > 0) {
      this.dialogService.openMessageDialog('Warning!', `Please fill all details for teeth: ${incompleteTeeth.join(', ')}`).subscribe();
      return false;
    }

    return true;
  }

  validateToothSelectionForSpecificTooth(toothData: ToothDataNew[], onComplete: () => void): void {
    if (this.abutmentSelectedTeeth.length === 0) {
      this.dialogService.openMessageDialog('Selection Required', 'Please select at least one tooth to configure.').subscribe();
      return;
    }

    const incompleteTeeth: number[] = [];
    this.abutmentSelectedTeeth.forEach(toothNum => {
      const td = toothData.find(t => t.teeth === toothNum);
      const isMissingSteps = !td || !td['abutmentStep1'] || !td['abutmentStep2'] || !td['abutmentStep3'] || !td['abutmentStep4'];
      if (isMissingSteps) incompleteTeeth.push(toothNum);
    });

    if (incompleteTeeth.length > 0) {
      this.dialogService.openMessageDialog('Incomplete Steps', `Please complete all 4 steps for tooth: ${incompleteTeeth.join(', ')}`).subscribe();
    } else {
      onComplete();
    }
  }

  validateAndProceed(toothData: ToothDataNew[]): void {
    if (!this.abutmentSelectedTeeth || this.abutmentSelectedTeeth.length === 0) {
      this.dialogService.openMessageDialog('Warning!', 'Please select at least one tooth.').subscribe();
      return;
    }

    const newlyCompletedTeeth: number[] = [];
    const incompleteTeeth: number[] = [];

    const filledTooth = toothData?.find(t =>
      this.abutmentSelectedTeeth.includes(t.teeth) &&
      t['abutmentStep1'] != null && t['abutmentStep2'] != null &&
      t['abutmentStep3'] != null && t['abutmentStep4'] != null
    );

    this.abutmentSelectedTeeth.forEach(toothNum => {
      const td = toothData?.find(t => t.teeth === toothNum);
      if (!td) return;
      const isEmpty = !td['abutmentStep1'] && !td['abutmentStep2'] && !td['abutmentStep3'] && !td['abutmentStep4'];
      if (isEmpty && filledTooth) {
        td['abutmentStep1'] = filledTooth['abutmentStep1'];
        td['abutmentStep2'] = filledTooth['abutmentStep2'];
        td['abutmentStep3'] = filledTooth['abutmentStep3'];
        td['abutmentStep4'] = filledTooth['abutmentStep4'];
      }
    });

    this.abutmentSelectedTeeth.forEach(toothNum => {
      const td = toothData?.find(t => t.teeth === toothNum);
      const isComplete = td && td['abutmentStep1'] != null && td['abutmentStep2'] != null && td['abutmentStep3'] != null && td['abutmentStep4'] != null;
      if (isComplete) {
        if (!this.submittedTeeth.includes(toothNum)) newlyCompletedTeeth.push(toothNum);
      } else {
        incompleteTeeth.push(toothNum);
      }
    });

    this.submittedTeeth = [...new Set([...this.submittedTeeth, ...newlyCompletedTeeth])];

    if (incompleteTeeth.length > 0) {
      this.dialogService.openMessageDialog('Warning!', `Please complete all details for teeth: ${incompleteTeeth.join(', ')}`).subscribe();
      return;
    }

    const abutmentTeethNums = this.abutmentTeeth.map(t => t.teeth);
    const isSameSelection = abutmentTeethNums.length === this.abutmentSelectedTeeth.length &&
      abutmentTeethNums.every(t => this.abutmentSelectedTeeth.includes(t));
    const allDataFilled = abutmentTeethNums.every(toothNum => {
      const td = toothData.find(t => t.teeth === toothNum);
      return td && td['abutmentStep1'] != null && td['abutmentStep2'] != null && td['abutmentStep3'] != null && td['abutmentStep4'] != null;
    });

    if ((isSameSelection && allDataFilled) || this.submittedTeeth.length === this.abutmentTeeth.length) {
      this.dialogService.openMessageDialog('Success!', `All selected teeth (${abutmentTeethNums.join(', ')}) saved successfully`).subscribe();
    } else if (newlyCompletedTeeth.length > 0) {
      this.dialogService.openMessageDialog('Success!', `Tooth ${this.abutmentSelectedTeeth.join(', ')} data updated successfully`).subscribe();
    }
  }
}
